import React from 'react';
import {
  Users,
  FileText,
  Eye,
  Zap,
  ThumbsUp,
  MessageSquareTextIcon,
  Share2,
  HandCoins,
  IndianRupee,
} from 'lucide-react';
import KPICard from '../metrics/KPICard';
import type { SharedMetricsData } from '../../types/sharedAnalytics.types';

export type { SharedMetricsData };

export interface SharedAnalyticsMetricsProps {
  metrics: SharedMetricsData;
}

export const SharedAnalyticsMetrics: React.FC<SharedAnalyticsMetricsProps> = ({ metrics }) => {
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Row 1 & Row 2: 4 columns each */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <KPICard
          title="Influencers"
          value={String(metrics.influencers)}
          icon={Users}
          iconBg="bg-[#4648D4]/10"
          iconColor="text-[#4648D4]"
          barGradient="bg-[#4648D4]"
          info="Number of creators currently active in this campaign."
        />
        <KPICard
          title="Posts"
          value={String(metrics.posts)}
          icon={FileText}
          iconBg="bg-[#4648D4]/10"
          iconColor="text-[#4648D4]"
          barGradient="bg-[#4648D4]"
          info="Total content pieces published by creators so far."
        />
        <KPICard
          title="Views"
          value={metrics.views}
          icon={Eye}
          iconBg="bg-[#4648D4]/10"
          iconColor="text-[#4648D4]"
          barGradient="bg-[#4648D4]"
          info="Total views generated across all campaign content."
        />
        <KPICard
          title="Engagement"
          value={metrics.engagement}
          icon={Zap}
          iconBg="bg-[#B4136D]/10"
          iconColor="text-[#B4136D]"
          barGradient="bg-[#B4136D]"
          info="Total interactions (likes, comments, shares, saves) across all posts."
        />

        <KPICard
          title="Avg Engagement Rate"
          value={metrics.engagementRate}
          icon={Zap}
          iconBg="bg-[#B4136D]/10"
          iconColor="text-[#B4136D]"
          barGradient="bg-[#B4136D]"
          info="Total interactions divided by total creator followers."
        />
        <KPICard
          title="Followers Reached"
          value={metrics.followers}
          icon={Users}
          iconBg="bg-[#4648D4]/10"
          iconColor="text-[#4648D4]"
          barGradient="bg-[#4648D4]"
          info="Combined audience size across all creators in this campaign."
        />
        <KPICard
          title="Est. Cost Per View"
          value={metrics.cpv}
          icon={HandCoins}
          iconBg="bg-[#059669]/10"
          iconColor="text-[#059669]"
          barGradient="bg-[#059669]"
          info="Estimated spend per video view delivered."
        />
        <KPICard
          title="Est. Cost Per Engagement"
          value={metrics.cpe}
          icon={IndianRupee}
          iconBg="bg-[#B4136D]/10"
          iconColor="text-[#B4136D]"
          barGradient="bg-[#B4136D]"
          info="Estimated spend per interaction generated."
        />
      </div>

      {/* Row 3: Total Likes, Total Comments, Total Shares — 3 columns */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        <KPICard
          title="Total Likes"
          value={metrics.totalLikes}
          icon={ThumbsUp}
          iconBg="bg-[#B4136D]/10"
          iconColor="text-[#B4136D]"
          barGradient="bg-[#B4136D]"
          info="Sum of all likes across campaign posts."
        />
        <KPICard
          title="Total Comments"
          value={metrics.totalComments}
          icon={MessageSquareTextIcon}
          iconBg="bg-[#4648D4]/10"
          iconColor="text-[#4648D4]"
          barGradient="bg-[#4648D4]"
          info="Sum of all comments across campaign posts."
        />
        <KPICard
          title="Total Shares"
          value={metrics.totalShares}
          icon={Share2}
          iconBg="bg-[#B4136D]/10"
          iconColor="text-[#B4136D]"
          barGradient="bg-[#B4136D]"
          info="Sum of all reshares across campaign posts."
        />
      </div>
    </div>
  );
};

export default SharedAnalyticsMetrics;
